import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse, shouldReportError } from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { DashboardRecord, UpdateDashboardRequest } from "../DashboardRecord";

import { dashboardQueryKeys } from "./dashboardQueryKeys";

export function useUpdateDashboard() {
  const { dashboardService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      dashboardId: string;
      updates: UpdateDashboardRequest;
    }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return dashboardService.updateDashboard(
        request.dashboardId,
        request.updates,
        currentOrganization.org_id,
      );
    },
    onSuccess: (record) => {
      queryClient.setQueryData(
        dashboardQueryKeys.id(record.dashboard_id, record.org_id),
        record,
      );
      // The list cache is InfiniteData (`useDashboards` walks the server's
      // cursor chain), so the record is patched in place on whichever page
      // holds it — every other page is preserved untouched.
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<DashboardRecord>>
      >(dashboardQueryKeys.list(record.org_id), (oldData) => {
        if (!oldData) {
          return undefined;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.map((existing) =>
              existing.dashboard_id === record.dashboard_id ? record : existing,
            ),
          })),
        };
      });
    },
    onError: (error) => {
      // A revision conflict is a routine, user-resolvable outcome with its own
      // dialog — reporting it would page someone every time two people edit the
      // same dashboard.
      if (shouldReportError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
